import { BookOpenIcon, LogOutIcon, SettingsIcon } from 'lucide-solid';
import { Localized } from '~/i18n';
import styles from './header.module.scss';
import NamedIcon from '~/components/ui/NamedIcon';
import {
  EnvironmentSwitcher,
  WorkspaceSwitcher
} from 'src/components/Layout/Header/SwitcherButtons';
import { useAppData } from '~/components/AppData';
import LogoutButton from '~/components/Layout/Header/LogoutButton.tsx';
import Menu from '~/components/ui/Menu';
import Link from '~/components/Link';
import { getSlug, MyAccountPagePath } from '~/components/AppRouter/utils.ts';
import { EnvironmentTypes } from '~/api';
import { externalLinks } from '~/consts.ts';
import ScalekitCautionWarning from '~/components/Layout/Header/ScalekitCautionWarning.tsx';
import { createSignal, Show } from 'solid-js';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { twMerge } from 'tailwind-merge';
import NeedHelpWithAuth from '~/components/NeedHelpWithAuth';
import { shouldShowInProductBanner } from '~/components/Layout/Header/utils.ts';
import SkLoader from '~/components/ui/SkLoader';

export default function Header() {
  const appData = useAppData();
  const [isLogoutLoading, setIsLogoutLoading] = createSignal<boolean>(false);

  return (
    <>
      <header
        class={`${styles.topNavbar}`}
        classList={{
          'border-b-yellow-500':
            appData.selectedEnvironment?.type !== EnvironmentTypes.PRODUCTION,
          'border-b-green-600':
            appData.selectedEnvironment?.type === EnvironmentTypes.PRODUCTION,
          [styles.pushBelowInproductBanner]: shouldShowInProductBanner({
            workspace: appData.workspace!,
            environment: appData.selectedEnvironment!
          })
        }}
      >
        <div
          id="header-leftSection"
          class={styles.leftSection}
        >
          {/*<div class={styles.logoContainer}>*/}
          {/*  <NamedIcon*/}
          {/*    w={32}*/}
          {/*    name={appData.workspace?.displayName || 'W'}*/}
          {/*  />*/}
          {/*</div>*/}
          <div class="flex items-center">
            <WorkspaceSwitcher>
              <div class={`flex items-center`}>
                <NamedIcon
                  w={30}
                  name={appData.workspace?.displayName || 'Workspace Name'}
                  class={`mr-2 workspace-logo`}
                />
                {appData.workspace?.displayName}
              </div>
            </WorkspaceSwitcher>
            <span class={`mx-1.5`}> / </span>
            <EnvironmentSwitcher />
          </div>
        </div>
        <ScalekitCautionWarning />
        <div class={`flex items-center`}>
          <nav class={`flex items-center`}>
            <Show
              when={isFeatureEnabled(
                FeatureFlagKeys.NEED_HELP_WITH_REVENUE_HERO
              )}
            >
              <NeedHelpWithAuth />
            </Show>
            <button
              class={`mr-4 px-3 py-1 text-sm font-normal hover:bg-bg-muted rounded-md transition-colors delay-75 border border-border-subtle`}
              role={`link`}
              data-testid={`docs-header-nav`}
            >
              <a
                href={externalLinks.kbArticles.root}
                class={`flex items-center`}
                target={'_blank'}
              >
                <BookOpenIcon
                  width={14}
                  class={`mr-1 mt-0.5`}
                  strokeWidth={2}
                />
                <Localized translationKey={`cta_labels.documentation`} />
              </a>
            </button>

            <Menu
              id={`workspace-switcher`}
              closeOnSelect={false}
              groups={[
                {
                  label: (
                    <div
                      class={`flex flex-col text-left text-sm font-normal text-fg-muted`}
                    >
                      <div class={`text-fg-muted`}>
                        {appData.member?.firstName} {appData.member?.lastName}
                      </div>
                      <div>{appData.member?.email}</div>
                    </div>
                  ),
                  items: [
                    {
                      label: (
                        <Link
                          class={`flex items-center font-normal w-full h-full`}
                          href={getSlug.myAccount(
                            MyAccountPagePath.MYACCOUNT_PROFILE,
                            true
                          )}
                        >
                          <SettingsIcon
                            class={`mr-2 w-5 h-5`}
                            strokeWidth={1}
                          />
                          <Localized
                            translationKey={'page_titles.profile_settings'}
                          />
                        </Link>
                      ),
                      dataTestId: `user-profile-settings`
                    },
                    {
                      label: (
                        <LogoutButton
                          class={`h-full`}
                          handleIsLogoutLoading={setIsLogoutLoading}
                        >
                          <LogOutIcon
                            class={`mr-2 w-5 h-5`}
                            strokeWidth={1}
                          />
                          <Localized translationKey={'page_titles.logout'} />
                          <Show when={isLogoutLoading()}>
                            <div class={`relative ml-auto mt-4`}>
                              <SkLoader />
                            </div>
                          </Show>
                        </LogoutButton>
                      ),
                      dataTestId: `logout`
                    }
                  ]
                }
              ]}
              containerClass={'p-4'}
            >
              {(props) => (
                <NamedIcon
                  elementAttributes={props}
                  w={32}
                  rounded
                  name={appData.member?.firstName}
                  class={twMerge(props?.class, `cursor-pointer bg-fg-default`)}
                  dataTestId={`user-profile-icon-header-nav`}
                />
              )}
            </Menu>
          </nav>
        </div>
      </header>
    </>
  );
}
